import { JSX, useEffect, useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { TranslationService } from '../../services/translation.service';
import { useTexts } from '../../hooks/texts.hook';
import { SanitizerService } from '../../services/sanitizer.service';
import FromToSelector, { type IFromToSelector, type IFromToSelectorState } from '../Shared/FromToSelector';
import { LocationType } from '../../models/location-type.enum';
import ValidationInfo from '../../validation/validation-info';
import { AnalyticsService } from '../../services/analytics-service';
import TimeModeSelector from '../Shared/TimeModeSelector';
import { TimeMode } from '../Shared/TimeModeSelector/time-mode.enum';
import type { IDateTimePickerState } from '../Shared/DateTimePicker';
import DateTimePicker from '../Shared/DateTimePicker';
import { ConfigurationService } from '../../services/configuration.service';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import type { IJourney } from '../../models/journey.model';
import { ApiPlaneraResaService } from '../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../helpers/error-response.helper';
import { DateTimeRelatesTo } from '../../models/date-time-relates-to.enum';
import { JourneyHelper } from '../../helpers/journey.helper';
import Spinner from '../Shared/Spinner';
import Alert from '../Shared/Alert';
import { AlertType } from '../Shared/Alert/models/alert.model';
import PaginatedList from '../Shared/JourneySearchResults/PaginatedList';
import DateHeader from '../Shared/JourneySearchResults/DateHeader';
import ExpandableListItem from '../Shared/JourneySearchResults/ExpandableListItem';
import JourneySummary from '../Shared/JourneySearchResults/JourneySummary';
import TravelPlannerFilters from './components/TravelPlannerFilters';
import {
  initialTravelPlannerFilters,
  isValidTravelPlannerFilters,
  sanitizeParsedTravelPlannerFilters,
  type ITravelPlannerFilters,
} from './components/TravelPlannerFilters/models/travel-planner-filters.model';
import { TransportMode } from '../../models/transport-mode.enum';
import { InterchangeDuration } from './components/TravelPlannerFilters/models/interchange-duration.enum';
import { DateHelper } from '../../helpers/date.helper';
import { type IErrorResponse, ErrorLevel } from '../../models/error-response.model';
import { ErrorHandlerHelper } from '../../helpers/error-handler.helper';
import { Status } from '../../hooks/fetch.hook';
import { JourneySearchHelper } from '../../helpers/journey-search.helper';
import JourneyDetails from './components/JourneyDetails';
import { IPaginationLinks } from '../../models/pagination-links.model';
import { ClientSideStorageItems, ClientSideStorageService } from '../../services/client-side-storage.service';
import { ClientSideStorageType } from '../../models/client-side-storage-type.enum';
import { CompareHelper } from '../../helpers/compare.helper';
import LogService from '../../services/log-service/log.service';
import React from 'react';
import { QueryStringHelper } from '../../helpers/query-string.helper';

function isTravelPlannerFiltersSaveEnabled() {
  return ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional) && ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave) !== '0';
}

function getTravelPlannerFilterFromLocalStorageMergedWith(initialTravelPlannerFilters: ITravelPlannerFilters) {
  if (!isTravelPlannerFiltersSaveEnabled()) return initialTravelPlannerFilters;

  const travelPlannerFilters = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters);
  if (travelPlannerFilters) {
    try {
      const parsedFilters = sanitizeParsedTravelPlannerFilters(JSON.parse(travelPlannerFilters));
      return isValidTravelPlannerFilters(parsedFilters) ? { ...initialTravelPlannerFilters, ...(parsedFilters as object) } : initialTravelPlannerFilters;
    } catch (error) {
      LogService.logError(error, 'Error parsing travel planner filters from local storage');
    }
  }
  return initialTravelPlannerFilters;
}

function TravelPlanner({ initialShowSearchDetails, headingText, headerHtml }: { initialShowSearchDetails: boolean; headingText?: string; headerHtml?: string }) {
  // === CONFIGURATION ===
  const { t } = useTexts();
  const currentInitialTravelPlannerFilters = getTravelPlannerFilterFromLocalStorageMergedWith(initialTravelPlannerFilters);

  // === STATE ===
  const [fromToSelectorState, setFromToSelectorState] = useState<IFromToSelectorState>({
    value: {
      fromLocation: undefined,
      toLocation: undefined,
    },
    validationInfo: new ValidationInfo(),
  });
  const [selectedTimeMode, setSelectedTimeMode] = useState<TimeMode>(TimeMode.now);
  const [dateTimePickerState, setDateTimePickerState] = useState<IDateTimePickerState>({
    value: DateHelper.roundToNearestFiveMinutes(DateTime.now()),
    validationInfo: new ValidationInfo(),
  });
  const [selectedTravelPlannerFilters, setSelectedTravelPlannerFilters] = useState<ITravelPlannerFilters>(currentInitialTravelPlannerFilters);
  const [mostRecentlyUsedFilters, setMostRecentlyUsedFilters] = useState<ITravelPlannerFilters>(currentInitialTravelPlannerFilters);
  const [showSearchDetails, setShowSearchDetails] = useState<boolean>(initialShowSearchDetails);
  const [searchJourneysStatus, setSearchJourneysStatus] = useState<Status>(Status.notperformed);
  const [receivedJourneys, setReceivedJourneys] = useState<IJourney[]>([]);
  const [receivedPaginationLinks, setReceivedPaginationLinks] = useState<IPaginationLinks | undefined>(undefined);
  const [isSearchingPrevious, setIsSearchingPrevious] = useState<boolean>(false);
  const [isSearchingNext, setIsSearchingNext] = useState<boolean>(false);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);
  const [isReconstructionSearch, setIsReconstructionSearch] = useState<boolean>(false);
  const [hasLocalService, setHasLocalService] = useState<boolean>(false);

  // === VARIABLES ===
  const validityTimeUntil = ConfigurationService.get('travelplanner.validitytimeuntil');
  const minDateTime = DateTime.now().startOf('day').minus({ days: 1 });
  const maxDateTime = validityTimeUntil ? DateTime.fromISO(validityTimeUntil) : DateTime.now().startOf('day').plus({ days: 90 });
  const currentFromToHasLocalService = fromToSelectorState.value.fromLocation?.location?.hasLocalService || fromToSelectorState.value.toLocation?.location?.hasLocalService;
  const isNoModeSelected = selectedTravelPlannerFilters.transportModes.length === 0 && !selectedTravelPlannerFilters.showWalking && !selectedTravelPlannerFilters.showBiking;
  const uniqueJourneyDates = JourneyHelper.getAllUniqueDates(receivedJourneys);
  const localServiceHtmlText = TranslationService.get('travelplanner.localservicetext');
  const isReconstructionError =
    searchJourneysStatus === Status.failed && errorResponse?.statusCode === 400 && isReconstructionSearch && (errorResponse.errorCode === 1003 || errorResponse.errorCode === 5001);

  // === REFS ===
  const fromToSelectorRef = useRef<IFromToSelector>(null!);

  // === USE EFFECTS ===
  // NV-8305: Check for reconstruction reference in query string and fetch journey if it exists. This is used for reconstructing a journey search from a shared link.
  useEffect(() => {
    const [reconstructionReference] = QueryStringHelper.getValuesFromQueryString('ref');
    if (!reconstructionReference) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsReconstructionSearch(true);
    setSearchJourneysStatus(Status.loading);
    ApiPlaneraResaService.getJourneyByReconstructionReference(reconstructionReference)
      .then((result) => {
        setReceivedJourneys(JourneyHelper.setJourneyIds([result], 0, false));

        const { fromLocation, toLocation } = JourneyHelper.resolveFromToLocations(result);
        if (fromLocation && toLocation) setFromToSelectorState((prev) => ({ ...prev, value: { fromLocation, toLocation } }));

        setSearchJourneysStatus(Status.success);
        AnalyticsService.addEvent(AnalyticsService.JourneySearchCategory, 'Sök resa via delad länk', '');
      })
      .catch((error: unknown) => {
        if (ErrorHandlerHelper.isErrorResponse(error)) {
          setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
        }
        setSearchJourneysStatus(Status.failed);
      });
  }, []);

  // === HELPER FUNCTIONS ===
  function getScreenReaderSearchStatus() {
    if (searchJourneysStatus === Status.notperformed) {
      return '';
    }
    if (searchJourneysStatus === Status.loading) {
      return TranslationService.get('common.searching');
    }
    return TranslationService.get('common.showingxresults').replace('{0}', (receivedJourneys.length ? receivedJourneys.length : 0).toString());
  }

  function getValidatableComponents(): ValidatableComponent[] {
    return [{ component: fromToSelectorRef.current, validationInfo: fromToSelectorState.validationInfo, setState: setFromToSelectorState }];
  }

  function showFeedbackForm() {
    const feedbackBlock = document.getElementById('travel-planner-feedback-form-block');
    if (feedbackBlock) {
      // Show feedback form
      document.getElementById('travel-planner-feedback-form-block')?.classList.remove('d-none');

      // Bind click event to close button
      document.getElementById('travel-planner-feedback-form-block-close-btn')?.addEventListener('click', () => {
        feedbackBlock.classList.add('d-none');
      });

      // Check if "thank you" message is shown, then hide it and show feedback form main content/question
      const feedbackStatusMessage = document.getElementsByClassName('Form__Status__Message')[0] as HTMLElement;
      if (feedbackStatusMessage && !feedbackStatusMessage.classList.contains('hide')) {
        const feedbackMainContent = document.getElementsByClassName('Form__MainBody')[0] as HTMLElement;
        if (feedbackMainContent) {
          // Show feedback form main content/question
          feedbackMainContent.style.display = '';
          // Hide status message
          feedbackStatusMessage.classList.add('hide');
          feedbackStatusMessage.classList.remove('Form__Success__Message');
        }
      }
    }
    document.dispatchEvent(new CustomEvent('show-travel-planner-feedback-area'));
  }

  // === EVENT HANDLERS ===
  async function handleSearchClick(): Promise<void> {
    if (searchJourneysStatus === Status.loading) return;
    if (isNoModeSelected) return;

    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      setSearchJourneysStatus(Status.notperformed);
      return;
    }

    setSearchJourneysStatus(Status.loading);
    setIsReconstructionSearch(false);
    fromToSelectorRef.current?.addSelectedItemsToAutocompleteHistory();
    setReceivedJourneys([]);
    setReceivedPaginationLinks(undefined);
    AnalyticsService.addEvent(AnalyticsService.JourneySearchCategory, 'Sök resa', '');

    const fromLocationDetails = fromToSelectorState.value.fromLocation ? JourneySearchHelper.getLocationParameters(fromToSelectorState.value.fromLocation) : undefined;
    const toLocationDetails = fromToSelectorState.value.toLocation ? JourneySearchHelper.getLocationParameters(fromToSelectorState.value.toLocation) : undefined;

    try {
      if (!fromLocationDetails || !toLocationDetails) {
        throw new Error('From and to locations are required.');
      }

      const result = await ApiPlaneraResaService.getJourneys(
        fromLocationDetails.gid,
        fromLocationDetails.name,
        fromLocationDetails.latitude,
        fromLocationDetails.longitude,
        toLocationDetails.gid,
        toLocationDetails.name,
        toLocationDetails.latitude,
        toLocationDetails.longitude,
        selectedTimeMode === TimeMode.now ? undefined : dateTimePickerState.value,
        selectedTimeMode === TimeMode.arrival ? DateTimeRelatesTo.arrival : DateTimeRelatesTo.departure,
        undefined,
        undefined,
        [
          ...selectedTravelPlannerFilters.transportModes,
          ...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []),
          ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : []),
        ],
        selectedTravelPlannerFilters.transportSubModes,
        selectedTravelPlannerFilters.onlyDirectConnections,
        selectedTravelPlannerFilters.viaStopArea?.gid,
        selectedTravelPlannerFilters.interchangeDuration === InterchangeDuration.short ? 2 : undefined,
        selectedTravelPlannerFilters.includeNearbyStopAreas,
        [...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []), ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : [])],
        selectedTravelPlannerFilters.bikingMaxDistance,
        selectedTravelPlannerFilters.bikingSpeed,
        selectedTravelPlannerFilters.showOccupancy,
      );

      setReceivedJourneys(JourneyHelper.setJourneyIds(result.results, 0, false));
      setHasLocalService(currentFromToHasLocalService ?? false);
      setReceivedPaginationLinks(result.links);
      setSearchJourneysStatus(Status.success);
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      AnalyticsService.addEvent(AnalyticsService.JourneySearchCategory, AnalyticsService.ErrorAction, `Sök resa - ${AnalyticsService.InternalErrorLabel}`);
      if (ErrorHandlerHelper.isAprErrorSignificant(error)) {
        LogService.logError(error, 'Error occurred while searching for journeys in Travel Planner');
      }
    } finally {
      setMostRecentlyUsedFilters(selectedTravelPlannerFilters);
      showFeedbackForm();
    }
  }

  async function handlePreviousClick(): Promise<void> {
    if (receivedPaginationLinks?.previous === undefined) return;
    try {
      setIsSearchingPrevious(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.previous);
      setReceivedJourneys((ps) => JourneyHelper.setJourneyIds(result.results, ps.length > 0 ? ps[0].id - 1 : 0, true).concat(ps));
      setReceivedPaginationLinks((ps) => (ps !== undefined ? { ...ps, previous: result.links?.previous } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      if (ErrorHandlerHelper.isAprErrorSignificant(error)) {
        LogService.logError(error, 'Error occurred while fetching previous journeys in Travel Planner');
      }
    } finally {
      setIsSearchingPrevious(false);
    }
  }

  async function handleNextClick(): Promise<void> {
    if (receivedPaginationLinks?.next === undefined) return;
    try {
      setIsSearchingNext(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.next);
      setReceivedJourneys((ps) => ps.concat(JourneyHelper.setJourneyIds(result.results, ps.length > 0 ? ps[ps.length - 1].id + 1 : 0, false)));
      setReceivedPaginationLinks((ps) => (ps !== undefined ? { ...ps, next: result.links?.next } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      if (ErrorHandlerHelper.isAprErrorSignificant(error)) {
        LogService.logError(error, 'Error occurred while fetching next journeys in Travel Planner');
      }
    } finally {
      setIsSearchingNext(false);
    }
  }

  function saveTravelPlannerFilters(filtersToSave: ITravelPlannerFilters) {
    if (isTravelPlannerFiltersSaveEnabled() && filtersToSave) {
      const difference = CompareHelper.getDifferences(initialTravelPlannerFilters, filtersToSave, ['viaStopArea']);
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters, JSON.stringify(difference));
    }
  }

  function handleTravelPlannerFilterChange(newFilters: ITravelPlannerFilters) {
    saveTravelPlannerFilters(newFilters);
    setSelectedTravelPlannerFilters(newFilters);
  }

  function handleTravelPlannerSaveSelectionsChange(newValue: boolean) {
    if (!newValue) {
      ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters);
    } else {
      saveTravelPlannerFilters(selectedTravelPlannerFilters);
    }
  }

  function getResultList(date: DateTime): JSX.Element {
    return (
      <>
        <li>
          <DateHeader date={date} uniqueJourneyDates={uniqueJourneyDates} />
        </li>
        <li>
          <ul className="list-unstyled">
            {JourneyHelper.getJourneysForDate(receivedJourneys, date).map((journey, index) => (
              <ExpandableListItem
                key={journey.id}
                id={`result-item-${index}`}
                className={JourneyHelper.getTripLegIsCancelled(journey) || JourneyHelper.getTripLegIsPartCancelled(journey) ? 'expandable-list-item--gray' : ''}
                collapsedContent={<JourneySummary journey={journey} useEstimatedTime={true} showNotesIcons={true} />}
                onExpandChange={(toBeExpanded) => {
                  if (toBeExpanded) {
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy', '');
                  }
                }}
                renderExpandedContent={(isVisible) => <JourneyDetails journey={journey} isVisible={isVisible} />}
              ></ExpandableListItem>
            ))}
          </ul>
        </li>
      </>
    );
  }

  return (
    <div className="travel-planner position-relative" role="search" aria-label={TranslationService.get('travelplanner.searchlabel')}>
      <div className={`container travel-planner__search-form pb-3 pt-3 pt-md-5 ${showSearchDetails ? 'pb-md-0' : 'pb-md-5'}`}>
        <div className="row justify-content-lg-center">
          <div className="col-lg-8">
            <div className="px-3 px-md-0 py-3 py-md-0 travel-planner__search-form-inner-wrapper rounded-xl bg-white">
              <div className={`px-md-4`}>
                <div>
                  {headingText && (
                    <div className="text-center pb-3 pb-sm-4">
                      <h1 className="m-0 travel-planner__heading pt-md-3">{headingText}</h1>
                    </div>
                  )}
                  {headerHtml && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(headerHtml) }} className="mb-3 pt-md-3"></div>}
                  <div className="mb-3 mb-md-0 pb-0 pb-md-4 m-0">
                    <FromToSelector
                      id={'from-to-selector'}
                      ref={fromToSelectorRef}
                      locationTypes={[LocationType.stopArea, LocationType.metaStation, LocationType.address, LocationType.pointOfInterest]}
                      placeholderText={TranslationService.get('travelplanner.stopaddresspoi')}
                      enableCurrentPosition={true}
                      analyticsCategory={AnalyticsService.TravelPlannerCategory}
                      state={fromToSelectorState}
                      onChange={(value) => setFromToSelectorState({ value, validationInfo: new ValidationInfo() })}
                      onFocus={() => setShowSearchDetails(true)}
                    />
                  </div>
                </div>
              </div>
              <div className={`px-md-4 pb-md-4 ${showSearchDetails ? '' : 'd-md-none'}`}>
                <div className="mb-3">
                  <TimeModeSelector
                    id="time-mode-selector"
                    availableTimeModes={[TimeMode.now, TimeMode.departure, TimeMode.arrival]}
                    selectedTimeMode={selectedTimeMode}
                    legend={TranslationService.get('travelplanner.selectdatetimeheading')}
                    onChange={(value) => {
                      setSelectedTimeMode(value);
                      AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'När vill du resa', value === TimeMode.now ? 'Nu' : value === TimeMode.departure ? 'Avgångstid' : 'Ankomsttid');
                    }}
                  />
                </div>
                {selectedTimeMode !== TimeMode.now && (
                  <div className="search-tools__pickers mb-3 mb-md-4">
                    <DateTimePicker
                      minDateTime={minDateTime}
                      maxDateTime={maxDateTime}
                      showAsteriskOnRequiredFields={false}
                      state={dateTimePickerState}
                      layoutSmRow={true}
                      onChange={(value) => {
                        setDateTimePickerState({ ...dateTimePickerState, value });
                      }}
                    />
                  </div>
                )}

                <TravelPlannerFilters
                  mostRecentlyUsed={mostRecentlyUsedFilters}
                  state={selectedTravelPlannerFilters}
                  onChange={handleTravelPlannerFilterChange}
                  onSaveSelectionsChange={handleTravelPlannerSaveSelectionsChange}
                />
                <div className="d-grid mx-auto col-md-7">
                  <button id="searchbutton" className="btn btn-primary" disabled={searchJourneysStatus === Status.loading} type="button" onClick={handleSearchClick}>
                    {TranslationService.get('common.searchtrip')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container travel-planner__results">
        <span className="visually-hidden" aria-live="polite">
          {getScreenReaderSearchStatus()}
        </span>
        {searchJourneysStatus === Status.loading && <Spinner className="mt-4" />}
        <div className="row justify-content-center">
          <Alert
            show={
              !isReconstructionError &&
              ((receivedJourneys.length === 0 && searchJourneysStatus === Status.success) || (searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.information))
            }
            heading={errorResponse?.errorMessage ?? t('Det finns inga resor på sträckan.', 'There are no trips for the search.')}
            headingTag="div"
            type={AlertType.Default}
            closable={false}
            className="mt-4 text-center col-lg-8"
          />
          <Alert
            show={isReconstructionError}
            heading={t('Referensen till resan du söker efter är inte giltig.', 'The reference for the journey you are searching for is not valid.')}
            headingTag="div"
            type={AlertType.Default}
            closable={false}
            className="mt-4 text-center col-lg-8"
          />
        </div>
        {searchJourneysStatus === Status.success && (
          <>
            {receivedJourneys.length > 0 && (
              <div className="mt-0 mt-md-3 ">
                <PaginatedList
                  className="fullwidth-sm"
                  previousButtonDisabled={receivedPaginationLinks?.previous === undefined}
                  nextButtonDisabled={receivedPaginationLinks?.next === undefined}
                  showSpinnerTop={isSearchingPrevious}
                  showSpinnerBottom={isSearchingNext}
                  onPreviousClick={handlePreviousClick}
                  onNextClick={handleNextClick}
                  preListItem={
                    hasLocalService &&
                    searchJourneysStatus === Status.success &&
                    localServiceHtmlText && <div className="text-center last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(localServiceHtmlText) }}></div>
                  }
                >
                  {uniqueJourneyDates.map((date) => (
                    <React.Fragment key={date.toISODate()}>{getResultList(date)}</React.Fragment>
                  ))}
                </PaginatedList>
              </div>
            )}
          </>
        )}
        {searchJourneysStatus === Status.success && receivedJourneys.length === 0 && hasLocalService && localServiceHtmlText && (
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="bg-blue border-blue p-2 mt-3">
                <div className="text-center last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(localServiceHtmlText) }}></div>
              </div>
            </div>
          </div>
        )}
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <Alert
              show={searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.error}
              heading={errorResponse?.errorMessage ?? t.generalError}
              closable={false}
              className="mt-4 text-center"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default TravelPlanner;
